import React, { useState } from 'react';
import { 
  TrendingUp, 
  Target, 
  ArrowRight, 
  CheckCircle2, 
  ChevronRight, 
  X, 
  Menu,
  Briefcase,
  Lightbulb,
  Users,
  Download,
  Compass,
  ShieldCheck,
  Zap,
  Handshake,
  MonitorSmartphone,
  LineChart,
  Database,
  Network
} from 'lucide-react';

// --- MAIN APP COMPONENT ---
export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);
  const [expandedCaseStudy, setExpandedCaseStudy] = useState(null);

  // Smooth scroll handler
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const CASE_STUDIES = [
    {
      num: "01",
      title: "Turnaround & Operational Excellence",
      framework: "5-Step Business Growth Strategy & TOC",
      overview: "Large-Scale Conventional Platinum Mining Operation (5,700+ Personnel, 3 Primary Shafts & Concentrator Plant).",
      painPoint: "Trapped in a volume-cost 'death spiral': a 28% production shortfall triggered a 13% spike in unit costs, squeezing cash flow. Strategy execution was bottlenecked.",
      agility: "Conducted rapid situational diagnostic and executive alignment sprints. Moved client from reactive firefighting to structured Lean Mining principles.",
      tech: "Designed blueprint for an Operational Excellence (OE) SBU supported by external BI system integration for live shift tracking and Value Driver Trees (VDTs).",
      impact: [
        "Quantified and aligned leadership around an actionable roadmap unlocking >R500 Million in annualized value leakage.",
        "Delivered a board-approved structural mandate for a dedicated OE unit.",
        "Stabilized operational metrics required to de-risk a multi-year Life of Mine (LOM) expansion program."
      ]
    },
    {
      num: "02",
      title: "Capital Restructuring & Turnkey Market Access",
      framework: "3-Step Capital Appraisal Engine & DCF Modeling",
      overview: "High-Capacity Architectural Timber & Door Manufacturer (2,000–4,000 Units/Day Throughput).",
      painPoint: "Suffered operational paralysis due to a historical internal fraud extraction of R6.2 million depleting working capital, leaving an annualized R36.7 Million order book unfulfilled.",
      agility: "Acted as an outsourced technical intermediary. Isolated issue as a liquidity constraint, mapped supplier integrations, and restructured governance with dual-authorization financial controls.",
      tech: "Constructed an institutional-grade 3-Statement Financial Model and DCF Valuation, proving intrinsic enterprise value. Automated raw material planning.",
      impact: [
        "Packaged a bankable R1,000,000 working capital facility to unpause 15 high-volume retail stores.",
        "Established a clear runway to generate R3.06M in monthly baseline revenue, scaling toward R42.8M by Year 3.",
        "Capitalized on Section 8(4) Buyer Power regulations to enforce 30-to-60 day settlement terms."
      ]
    },
    {
      num: "03",
      title: "Strategic Pivot & Financial Recovery",
      framework: "5-Step Business Growth Strategy (5Cs & Ansoff Alignment)",
      overview: "Heavy Industrial Engineering & Fluid Management Firm (68 Technical Personnel, Serving Blue-Chip Petrochem & Energy).",
      painPoint: "Acute financial distress with a net loss of R9.5 Million due to 'Strategic Drift'—chasing uncosted projects while absorbing a 38% raw material price shock.",
      agility: "Deployed an intensive 5Cs diagnostic, restructuring corporate operations into two distinct units: Precision Reliability (high-margin growth) and Infrastructure Solutions (controlled cost center).",
      tech: "Implemented the Xpanxn Project Costing Tool to enforce strict standard vs. actual cost monitoring (≤5% variance limit) on 100% of pipeline projects.",
      impact: [
        "Built a 3-year recovery budget restoring operating margins from -59.8% to +11.7%.",
        "Rationalized operating overheads down from 50% to ≤30% of sales.",
        "Enacted mandatory dual-sourcing and contract indexation clauses, safeguarding 68 jobs."
      ]
    },
    {
      num: "04",
      title: "Industrial Healthcare Expansion & Capital Memo",
      framework: "3-Step Capital Appraisal Engine & Integrated SBU Valuation",
      overview: "Specialized Occupational Health & Mobile Medical Surveillance Provider.",
      painPoint: "Operational throughput constrained at 60–70% capacity. Needed aggressive fleet expansion to meet MHSA compliance mandates without compromising working capital.",
      agility: "Mapped the 'clinical assembly line,' ring-fenced operational risk by isolating OpCo from Property Assets, and structured commercial case around regulatory moats.",
      tech: "Developed a comprehensive 8-year financial forecast model (FY2026–FY2033), cash flow waterfalls, and a detailed institutional Information Memorandum (IM).",
      impact: [
        "Structured and defended a R20 Million DFI/Senior Debt capitalization package to double fleet capacity.",
        "Outlined the roadmap to scale enterprise revenue to R75.8 Million by FY2033 while safeguarding a 46% EBITDA margin.",
        "Formulated working capital optimization policies to convert R4.7M in corporate receivables into liquid cash."
      ]
    },
    {
      num: "05",
      title: "B2B Revenue Syntropy & Commercial Sales Engine",
      framework: "The Revenue Syntropy Model & MEDDIC B2B Pipeline Architecture",
      overview: "Integrated HVAC & R Engineering Specialist (15-Year Legacy, Commercial & Industrial Sectors).",
      painPoint: "Commercially reactive, relying on word-of-mouth. Hybrid role blurring resulted in stagnant lead generation, margin discounting, and lumpy cash flow.",
      agility: "Dismantled hybrid setup and institutionalized a specialized commercial engine. Segregated 'Hunters' from 'Farmers' and ran a masterclass on Total Cost of Ownership (TCO) value-selling.",
      tech: "Deployed an omnichannel B2B sales funnel embedded with the MEDDIC qualification framework and integrated pipeline management dashboards to track MRR.",
      impact: [
        "100% executive and sales team adoption across consultative B2B deal qualification.",
        "Re-architected commercial pricing around a strict 20% Operating Profit margin threshold.",
        "Established dedicated outbound cadences to transition one-off transactional jobs into long-term commercial SLAs."
      ]
    },
    {
      num: "06",
      title: "Cross-Border Industrial Market Penetration",
      framework: "Differentiation Focus (Porter) & Ansoff Market Expansion",
      overview: "Specialized Drilling Consumables & Friction-Welding Engineering Manufacturer.",
      painPoint: "Exposed due to 90% historic client concentration with a single OEM. Direct expansion into formal mining houses was stalled.",
      agility: "Architected the low-risk 'Saver Sub Trojan Horse Strategy' to supply high-wear saver subs for site trials, creating an immediate wedge to upsell high-volume drill strings.",
      tech: "Configured the Xpanxn CRM and LinkedIn Sales Navigator lead capture engine targeting exploration executives. Digitized NDT data into buyer-enablement collateral.",
      impact: [
        "Formulated a 3-year plan driving revenue from R1.5M to R2.18M while locking in a minimum 15.3% ROCE.",
        "Unlocked regional B2B demand across the SADC copperbelt (Zambia & DRC).",
        "Successfully monetized idle plant capacity by packaging friction welding as an independent industrial service."
      ]
    },
    {
      num: "07",
      title: "Fast-Track Industrial Demand & Clearance Engine",
      framework: "The Revenue Syntropy Lead Generation Engine",
      overview: "Specialized Circular Textile & Knitting Mill (35+ Year Industrial Heritage).",
      painPoint: "Held over R2.24 Million in dormant, slow-moving fabric inventory due to legacy wholesale processes and lack of outbound commercial infrastructure.",
      agility: "Engineered and executed an outbound commercial roadmap in under 30 days. Positioned local agility against delayed Asian imports to intercept CMT operators.",
      tech: "Launched a digitized fabric showroom featuring an interactive online RFQ builder, multi-tier WhatsApp commercial integration, and an ABM outreach cadence.",
      impact: [
        "Activated a targeted liquidation campaign to liberate cash trapped in excess inventory.",
        "Deployed a weekly outbound ABM cadence driving direct sample distribution to qualified procurement buyers.",
        "Aligned mill with local retailer procurement initiatives, converting spare capacity into recurring orders."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans selection:bg-[#FF6B35] selection:text-white">
      
      {/* Floating WhatsApp Button */}
      <a 
        href="https://wa.me/27828949157" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="fixed bottom-6 right-6 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:bg-[#128C7E] transition-all hover:scale-110 z-50 flex items-center justify-center group"
        aria-label="Chat with us on WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.305-.885-.653-1.482-1.46-1.656-1.758-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>

      {/* Navigation */}
      <nav className="fixed w-full z-40 bg-[#0B132B]/95 backdrop-blur-md border-b border-[#1c2846] shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
              <img 
                src="Xpanxn logo 20251212.png" 
                alt="Xpanxn Logo" 
                className="h-12 w-auto object-contain brightness-0 invert"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span className="hidden text-2xl font-extrabold text-white tracking-tight">
                Xpanxn<span className="text-[#FF6B35]">.</span>
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollTo('about')} className="text-sm font-semibold text-slate-300 hover:text-[#FF6B35] transition-colors duration-300">About Us</button>
              <button onClick={() => scrollTo('value-prop')} className="text-sm font-semibold text-slate-300 hover:text-[#FF6B35] transition-colors duration-300">Value Prop</button>
              <button onClick={() => scrollTo('services')} className="text-sm font-semibold text-slate-300 hover:text-[#FF6B35] transition-colors duration-300">Services</button>
              <button onClick={() => scrollTo('how-we-work')} className="text-sm font-semibold text-slate-300 hover:text-[#FF6B35] transition-colors duration-300">How We Work</button>
              <button onClick={() => scrollTo('projects')} className="text-sm font-semibold text-slate-300 hover:text-[#FF6B35] transition-colors duration-300">Case Studies</button>
              <button 
                onClick={() => setIsAssessmentOpen(true)}
                className="bg-[#FF6B35] text-white px-6 py-2.5 rounded-full font-bold hover:bg-[#e85b28] transition-all shadow-md hover:shadow-lg hover:shadow-[#FF6B35]/30"
              >
                Free Assessment
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-slate-300 hover:text-white p-2 transition-colors">
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && (
          <div className="lg:hidden bg-[#0B132B] border-b border-[#1c2846] px-4 pt-2 pb-6 space-y-2 shadow-2xl">
            <button onClick={() => scrollTo('about')} className="block w-full text-left px-3 py-4 text-slate-300 font-semibold border-b border-[#1c2846] hover:text-[#FF6B35]">About Us</button>
            <button onClick={() => scrollTo('value-prop')} className="block w-full text-left px-3 py-4 text-slate-300 font-semibold border-b border-[#1c2846] hover:text-[#FF6B35]">Value Proposition</button>
            <button onClick={() => scrollTo('services')} className="block w-full text-left px-3 py-4 text-slate-300 font-semibold border-b border-[#1c2846] hover:text-[#FF6B35]">Services</button>
            <button onClick={() => scrollTo('how-we-work')} className="block w-full text-left px-3 py-4 text-slate-300 font-semibold border-b border-[#1c2846] hover:text-[#FF6B35]">How We Work</button>
            <button onClick={() => scrollTo('projects')} className="block w-full text-left px-3 py-4 text-slate-300 font-semibold border-b border-[#1c2846] hover:text-[#FF6B35]">Case Studies</button>
            <button 
              onClick={() => { setIsAssessmentOpen(true); setIsMenuOpen(false); }}
              className="w-full mt-6 bg-[#FF6B35] text-white px-5 py-4 rounded-xl font-bold shadow-md hover:bg-[#e85b28]"
            >
              Start Free Assessment
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-44 md:pb-32 px-4 bg-gradient-to-b from-[#f1f5f9] to-white overflow-hidden relative">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-slate-200 opacity-50 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-[#FF6B35]/10 opacity-50 blur-3xl"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-[#0B132B] mb-6 leading-tight hover:text-[#FF6B35] cursor-pointer transition-colors duration-300">
            Expansion <span className="text-[#FF6B35]">Made Simple.</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed font-medium">
            We unify Strategy Consulting and Business Development to accelerate growth for traditional, labour-consuming enterprises across Africa.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <button 
              onClick={() => setIsAssessmentOpen(true)}
              className="w-full sm:w-auto bg-[#0B132B] text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-[#1a2542] transition-all shadow-xl flex items-center justify-center group"
            >
              Start Free Assessment
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform text-[#FF6B35]" size={20} />
            </button>
            <button 
              onClick={() => scrollTo('about')}
              className="w-full sm:w-auto bg-white text-[#0B132B] border-2 border-slate-200 px-8 py-4 rounded-full text-lg font-bold hover:border-[#FF6B35] hover:text-[#FF6B35] transition-all shadow-sm"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center space-y-24">
            
            {/* Intro */}
            <div className="max-w-3xl">
              <h2 className="text-4xl font-extrabold text-[#0B132B] mb-6 hover:text-[#FF6B35] cursor-pointer transition-colors duration-300">About Us</h2>
              <p className="text-lg text-slate-600 leading-relaxed font-medium">
                Xpanxn Pty Ltd was registered in January 2025 with head offices in Johannesburg, South Africa. 
                Guided by a clear strategic identity, our founders recognized a massive execution gap between 
                the development of strategy and the actual acquisition of customers for medium to large entities. 
                Xpanxn is obsessed with simplifying complex challenges into repeatable steps that can be replicated to scale any business.
              </p>
            </div>

            {/* Vision */}
            <div className="max-w-4xl bg-slate-50 p-10 md:p-14 rounded-3xl border border-slate-100 shadow-sm hover:shadow-lg transition-all group">
              <Target size={48} className="text-[#FF6B35] mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="text-2xl md:text-3xl font-bold text-[#0B132B] mb-4 group-hover:text-[#FF6B35] transition-colors">Our Corporate Vision</h3>
              <p className="text-xl md:text-2xl text-slate-700 leading-relaxed font-light">
                "To accelerate industrial development and structural scaling across Africa by empowering traditional, labor-intensive enterprises to realize their full market and operational potential."
              </p>
            </div>

            {/* Mission */}
            <div className="max-w-4xl bg-[#0B132B] p-10 md:p-14 rounded-3xl shadow-xl hover:shadow-2xl hover:shadow-[#0B132B]/20 transition-all group">
              <TrendingUp size={48} className="text-[#FF6B35] mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Our Corporate Mission</h3>
              <p className="text-xl md:text-2xl text-slate-300 leading-relaxed font-light">
                "To simplify solutions to complex strategic, commercial, and financial challenges—unlocking predictable market access and bankable capital for growing, traditional, labor-consuming enterprises."
              </p>
            </div>

            {/* Values */}
            <div className="max-w-6xl w-full">
              <h3 className="text-3xl font-bold text-[#0B132B] mb-12 hover:text-[#FF6B35] cursor-pointer transition-colors duration-300">Our Core Values</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
                <div className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-[#FF6B35] hover:shadow-xl transition-all group">
                  <Compass className="text-[#0B132B] mb-5 group-hover:text-[#FF6B35] group-hover:animate-bounce" size={36} />
                  <h4 className="font-bold text-[#0B132B] mb-3 text-lg">Curiosity in Diagnosis</h4>
                  <p className="text-sm text-slate-600 leading-relaxed font-medium">We reject surface-level assumptions. We dive deep into the operational data to identify and treat the root causes of business stagnation, not just the symptoms.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-[#FF6B35] hover:shadow-xl transition-all group">
                  <ShieldCheck className="text-[#0B132B] mb-5 group-hover:text-[#FF6B35] group-hover:animate-bounce" size={36} />
                  <h4 className="font-bold text-[#0B132B] mb-3 text-lg">Integrity in Counsel</h4>
                  <p className="text-sm text-slate-600 leading-relaxed font-medium">We provide objective, honest, and realistic guidance centered on long-term sustainability, helping clients build robust foundations that lenders trust.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-[#FF6B35] hover:shadow-xl transition-all group">
                  <Zap className="text-[#0B132B] mb-5 group-hover:text-[#FF6B35] group-hover:animate-bounce" size={36} />
                  <h4 className="font-bold text-[#0B132B] mb-3 text-lg">Energy in Execution</h4>
                  <p className="text-sm text-slate-600 leading-relaxed font-medium">We are not bystander consultants. We bring high-tempo execution and structural momentum to ensure strategies transition into daily operations.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-[#FF6B35] hover:shadow-xl transition-all group">
                  <Handshake className="text-[#0B132B] mb-5 group-hover:text-[#FF6B35] group-hover:animate-bounce" size={36} />
                  <h4 className="font-bold text-[#0B132B] mb-3 text-lg">Service in Partnership</h4>
                  <p className="text-sm text-slate-600 leading-relaxed font-medium">We operate as a high-touch, long-term implementation partner. We intentionally limit our client intake to ensure personalized attention.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section id="value-prop" className="py-24 bg-slate-100 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0B132B] mb-6 hover:text-[#FF6B35] cursor-pointer transition-colors duration-300">Our Value Proposition</h2>
            <p className="text-xl text-[#FF6B35] font-bold mb-6 uppercase tracking-widest">High-Touch Consulting, Tech-Enabled Execution</p>
            <p className="text-lg text-slate-600 leading-relaxed font-medium">
              Xpanxn has taken a distinctly different position in the market. We don't just deliver static reports; we implement practical solutions utilizing a specialized technology stack to ensure complete transparency, streamlined execution, and measurable ROI.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white border-b-4 border-transparent hover:border-[#FF6B35] p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all group">
              <MonitorSmartphone className="text-[#0B132B] group-hover:text-[#FF6B35] mb-5 transition-colors" size={40} />
              <h4 className="font-bold text-[#0B132B] mb-3 text-xl">Xpanxn PM Tool</h4>
              <p className="text-sm text-slate-600 leading-relaxed font-medium">A dedicated project portal granting clients real-time access to track deliverables, milestones, and manage ongoing execution collaboratively.</p>
            </div>
            <div className="bg-white border-b-4 border-transparent hover:border-[#FF6B35] p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all group">
              <Database className="text-[#0B132B] group-hover:text-[#FF6B35] mb-5 transition-colors" size={40} />
              <h4 className="font-bold text-[#0B132B] mb-3 text-xl">Xpanxn CRM</h4>
              <p className="text-sm text-slate-600 leading-relaxed font-medium">Powered by robust frameworks, giving clients full visibility into active sales funnels, pipeline velocity, and B2B deal stages.</p>
            </div>
            <div className="bg-white border-b-4 border-transparent hover:border-[#FF6B35] p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all group">
              <LineChart className="text-[#0B132B] group-hover:text-[#FF6B35] mb-5 transition-colors" size={40} />
              <h4 className="font-bold text-[#0B132B] mb-3 text-xl">Xpanxn KPI Tracker</h4>
              <p className="text-sm text-slate-600 leading-relaxed font-medium">A custom SaaS application providing continuous ability to input and track operational, financial, and growth metrics across SBUs.</p>
            </div>
            <div className="bg-white border-b-4 border-transparent hover:border-[#FF6B35] p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all group">
              <Network className="text-[#0B132B] group-hover:text-[#FF6B35] mb-5 transition-colors" size={40} />
              <h4 className="font-bold text-[#0B132B] mb-3 text-xl">Lead Beacons</h4>
              <p className="text-sm text-slate-600 leading-relaxed font-medium">Advanced data-mining tools utilized to identify the exact decision-makers fitting your ICP, powering our precision prospecting engines.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#0B132B] mb-6 hover:text-[#FF6B35] cursor-pointer transition-colors duration-300">Services & Delivery Models</h2>
            <p className="text-lg text-slate-600 font-medium">
              We structure our advisory and execution through a clear 3-Tier Consulting Delivery Model, mapping directly to your organizational capacity and speed-to-ROI requirements.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-stretch">
            
            {/* TIER 1 */}
            <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200 flex flex-col hover:shadow-lg transition-shadow">
              <div className="mb-6">
                <span className="text-sm font-bold tracking-widest uppercase text-slate-500 mb-2 block">Tier 1</span>
                <h3 className="text-2xl font-bold text-[#0B132B]">Strategic Blueprints</h3>
                <p className="text-[#FF6B35] font-semibold mt-1">Do-It-Yourself / "I Show You"</p>
              </div>
              <p className="text-slate-600 mb-6 font-medium text-sm leading-relaxed flex-grow">
                Access to our battle-tested frameworks, diagnostic maturity audits, and self-guided toolkits. Perfect for resource-constrained teams or internal operators wanting proven roadmaps without full consulting overhead.
              </p>
              <div className="bg-white p-4 rounded-xl border border-slate-200 mb-6">
                <h4 className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide">Who It's For</h4>
                <p className="text-sm text-slate-600">Teams with dedicated in-house executors lacking a proven strategic framework.</p>
              </div>
              <button 
                onClick={() => scrollTo('how-we-work')}
                className="w-full bg-white text-[#0B132B] border-2 border-slate-300 py-3 rounded-xl font-bold hover:border-[#FF6B35] hover:text-[#FF6B35] transition-all"
              >
                Download Frameworks
              </button>
            </div>

            {/* TIER 2 */}
            <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200 flex flex-col hover:shadow-lg transition-shadow">
              <div className="mb-6">
                <span className="text-sm font-bold tracking-widest uppercase text-slate-500 mb-2 block">Tier 2</span>
                <h3 className="text-2xl font-bold text-[#0B132B]">Embedded Advisory</h3>
                <p className="text-[#FF6B35] font-semibold mt-1">Done-With-You / "I Do With You"</p>
              </div>
              <p className="text-slate-600 mb-6 font-medium text-sm leading-relaxed flex-grow">
                Active co-piloting via bi-weekly strategy sprints, joint pipeline reviews, and KPI tracking. We map our Growth Facilitation and Pipeline Coaching directly to your existing team to eliminate blind spots.
              </p>
              <div className="bg-white p-4 rounded-xl border border-slate-200 mb-6">
                <h4 className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide flex items-center"><MonitorSmartphone size={14} className="mr-2 text-[#0B132B]"/> Tech Integration</h4>
                <p className="text-sm text-slate-600">Shared Workspace Access via Xpanxn PM & KPI Tracker.</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 mb-6">
                <h4 className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide">Who It's For</h4>
                <p className="text-sm text-slate-600">Mid-market enterprises needing structural cadence and strategic oversight.</p>
              </div>
              <button 
                onClick={() => setIsAssessmentOpen(true)}
                className="w-full bg-[#0B132B] text-white py-3 rounded-xl font-bold hover:bg-[#1a2542] transition-all shadow-md"
              >
                Schedule Consultation
              </button>
            </div>

            {/* TIER 3 (FLAGSHIP) */}
            <div className="bg-[#0B132B] rounded-3xl p-8 border-2 border-[#FF6B35] shadow-2xl flex flex-col relative transform lg:-translate-y-4">
              <div className="absolute top-0 right-0 bg-[#FF6B35] text-white text-xs font-bold uppercase tracking-wider py-1 px-4 rounded-bl-xl rounded-tr-xl">
                Enterprise Flagship
              </div>
              <div className="mb-6 mt-2">
                <span className="text-sm font-bold tracking-widest uppercase text-slate-400 mb-2 block">Tier 3</span>
                <h3 className="text-2xl font-bold text-white">Turnkey Execution Engine</h3>
                <p className="text-[#FF6B35] font-semibold mt-1">Done-For-You / "I Do It For You"</p>
              </div>
              <p className="text-slate-300 mb-6 font-medium text-sm leading-relaxed flex-grow">
                Total operational takeover of the commercial function. We deliver Full Turnaround & Bankable Capital Appraisals alongside a Fully Outsourced Revenue Factory (Apollo/Sales Nav execution, CRM admin, performance commercials).
              </p>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 mb-6">
                <h4 className="text-xs font-bold text-white mb-2 uppercase tracking-wide flex items-center"><Database size={14} className="mr-2 text-[#FF6B35]"/> Tech Integration</h4>
                <p className="text-sm text-slate-300">Full Agency Orchestration (Xpanxn Tech Stack, CRM Admin, ABM Setup).</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 mb-8">
                <h4 className="text-xs font-bold text-white mb-2 uppercase tracking-wide">Who It's For</h4>
                <p className="text-sm text-slate-300">Enterprises (R50m–R500m) with maxed-out core teams needing an end-to-end partner to build, manage, and scale the engine.</p>
              </div>
              <button 
                onClick={() => setIsAssessmentOpen(true)}
                className="w-full bg-[#FF6B35] text-white py-4 rounded-xl font-extrabold hover:bg-[#e85b28] transition-all shadow-lg hover:shadow-[#FF6B35]/40 text-lg"
              >
                Apply for Dedicated Partnership
              </button>
            </div>

          </div>

          {/* Embedded Strategy Video */}
          <div className="mt-32 max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-[#0B132B] mb-6 hover:text-[#FF6B35] cursor-pointer transition-colors">Insights on Strategy Consulting</h3>
            <p className="text-slate-600 mb-10 font-medium">Watch our founder dissect the execution gap between boardroom strategy and factory-floor realities.</p>
            <div className="w-full aspect-video rounded-3xl overflow-hidden shadow-2xl border-4 border-slate-100 bg-slate-900 relative">
              <iframe 
                className="w-full h-full" 
                src="https://www.youtube.com/embed/nvdrFr0AGnI?si=Fg3Qn-YV1GPhMSEh" 
                title="Xpanxn Strategy Consulting Overview" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </div>
          </div>

        </div>
      </section>

      {/* How We Work Section */}
      <section id="how-we-work" className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#0B132B] mb-6 hover:text-[#FF6B35] cursor-pointer transition-colors duration-300">How We Work</h2>
            <p className="text-lg text-slate-600 font-medium">Transparent processes and structured pricing designed for measurable ROI across our strategic and business development units.</p>
          </div>

          <div className="space-y-16">
            {/* Strategy Consulting Timeline */}
            <div className="bg-white rounded-2xl p-8 shadow-sm flex flex-col xl:flex-row gap-8 items-center border border-slate-100">
              <div className="xl:w-1/3">
                <h3 className="text-2xl font-bold text-cyan-700 mb-2">Strategy Consulting</h3>
                <p className="text-slate-600 mb-4 font-medium">5-Week Business Growth Timeline.</p>
                <div className="bg-slate-50 p-4 rounded-lg mb-6 border border-slate-100">
                  <span className="text-sm font-bold text-slate-900 block mb-1">Pricing Guideline:</span>
                  <span className="text-sm text-slate-600">Fixed Project Fee based on enterprise size, or Monthly Retainer for ongoing execution monitoring.</span>
                </div>
                {/* Download Link */}
                <a 
                  href="Xpanxn 5 Step BGS Framework.jpg" 
                  download 
                  className="inline-flex items-center text-sm font-bold text-cyan-700 hover:text-cyan-800 bg-cyan-50 hover:bg-cyan-100 px-4 py-2 rounded-lg transition-colors border border-cyan-200"
                >
                  <Download size={16} className="mr-2" /> Download 5-Step Framework Diagram
                </a>
              </div>
              <div className="xl:w-2/3 grid grid-cols-2 md:grid-cols-5 gap-4 w-full h-full">
                {[
                  { name: 'Situational Analysis (5Cs & SWOT)', time: 'Week 1' }, 
                  { name: 'Shareholder Value', time: 'Week 2' }, 
                  { name: 'VMSO & Value Proposition', time: 'Week 3' }, 
                  { name: 'Winning Strategic Options', time: 'Week 4' }, 
                  { name: 'Performance & Budgeting (KPIs)', time: 'Week 5' }
                ].map((step, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-4 bg-slate-50 rounded-xl border border-slate-100 h-full group hover:border-cyan-300 transition-all hover:shadow-md">
                    <div className="w-12 h-12 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center font-bold mb-4 shrink-0 transition-transform group-hover:scale-110 duration-300">
                      <Target size={24} className="group-hover:animate-pulse" />
                    </div>
                    <span className="text-sm font-bold text-slate-900 mb-3 leading-tight group-hover:text-cyan-700 transition-colors">{step.name}</span>
                    <span className="text-xs text-cyan-700 font-semibold bg-cyan-100/50 px-2 py-1.5 rounded-md mt-auto w-full group-hover:bg-cyan-600 group-hover:text-white transition-colors">{step.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* BD Agency Timeline */}
            <div className="bg-white rounded-2xl p-8 shadow-sm flex flex-col xl:flex-row gap-8 items-start border border-slate-100">
              <div className="xl:w-1/3 xl:sticky xl:top-28">
                <h3 className="text-2xl font-bold text-blue-700 mb-2">Business Development</h3>
                <p className="text-slate-600 mb-4 font-medium">Revenue Engine Construction & Execution.</p>
                <div className="bg-slate-50 p-4 rounded-lg mb-6 border border-slate-100">
                  <span className="text-sm font-bold text-slate-900 block mb-1">Pricing Guideline:</span>
                  <span className="text-sm text-slate-600">Base Retainer (covers tool stack & management) + Performance Commission on closed deals.</span>
                </div>
                {/* Download Link */}
                <a 
                  href="Xpanxn BD Framework.png" 
                  download 
                  className="inline-flex items-center text-sm font-bold text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors border border-blue-200"
                >
                  <Download size={16} className="mr-2" /> Download BD Framework
                </a>
              </div>
              <div className="xl:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                {[
                  { name: 'Product-Market Fit Assessment', time: '30 Days', desc: 'Conduct the "Service Maturity Audit" to diagnose the client\'s structural health, establish baseline metrics, and classify the business as Pre-PMF (Validation) or Post-PMF (Scaling).' },
                  { name: 'Setting up Infrastructure', time: '30 Days', desc: 'Build the Revenue Syntropy engine. Equip the client with the necessary assets, configure the Xpanxn tech stack, and prepare for market outreach.' },
                  { name: 'Lead Generation', time: 'Recurring Activities', desc: 'Execute organic digital marketing and targeted Account-Based Marketing (ABM) to capture Marketing Qualified Leads (MQLs).' },
                  { name: 'Prospecting', time: 'Recurring Activities', desc: 'Transition MQLs into active Sales Qualified Leads (SQLs) through orchestrated nurturing sequences.' },
                  { name: 'Conversion', time: 'Recurring Activities', desc: 'Facilitate consultative evaluations and eliminate transactional friction to capture maximum pricing value.' },
                  { name: 'Retention', time: 'Recurring Activities', desc: 'Establish loyalty loops to maximize Net Revenue Retention (NRR) and drive sustainable profitability.' }
                ].map((step, idx) => (
                  <div key={idx} className="flex flex-col p-5 bg-slate-50 rounded-xl border border-slate-100 h-full hover:shadow-md transition-all group hover:border-blue-300">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold shrink-0 text-lg transition-transform group-hover:scale-110 duration-300">
                         <TrendingUp size={28} className="group-hover:animate-pulse" />
                      </div>
                      <span className="text-base font-bold text-slate-900 leading-tight group-hover:text-blue-700 transition-colors">{idx + 1}. {step.name}</span>
                    </div>
                    <p className="text-sm text-slate-600 mb-5 flex-grow font-medium leading-relaxed">{step.desc}</p>
                    <span className="text-xs text-blue-700 font-bold bg-blue-100/50 px-3 py-1.5 rounded-md self-start mt-auto uppercase tracking-wide group-hover:bg-blue-600 group-hover:text-white transition-colors">{step.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects & Case Studies */}
      <section id="projects" className="py-24 bg-[#0B132B] text-white border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">Proven Enterprise Transformations</h2>
            <p className="text-xl text-[#FF6B35] font-bold mb-6 uppercase tracking-widest">Case Studies</p>
            <p className="text-lg text-slate-300 font-medium leading-relaxed">
              Transforming Industrial Complexities into Measurable Commercial Value. Explore how Xpanxn deploys proprietary diagnostic frameworks, hands-on commercial intervention, and integrated technology to solve acute financial distress, unlock trapped capacity, and architect bankable transactions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {CASE_STUDIES.map((study, idx) => (
              <div key={idx} className="bg-slate-800 rounded-3xl p-8 border border-slate-700 hover:border-[#FF6B35] hover:shadow-2xl transition-all flex flex-col">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold text-white pr-4">Case Study {study.num}: {study.title}</h3>
                </div>
                <p className="text-sm text-[#FF6B35] font-bold mb-4 tracking-wide bg-[#FF6B35]/10 inline-block px-3 py-1 rounded-md self-start">
                  Framework: {study.framework}
                </p>
                <div className="text-slate-300 font-medium leading-relaxed mb-6">
                  <p><strong>Client Overview:</strong> {study.overview}</p>
                </div>

                {expandedCaseStudy === idx && (
                  <div className="mt-4 pt-6 border-t border-slate-700 space-y-6 animate-fade-in flex-grow">
                    <div>
                      <h4 className="text-lg font-bold text-white mb-2 flex items-center"><Target size={18} className="mr-2 text-[#FF6B35]"/> The Pain Point & Diagnostic</h4>
                      <p className="text-sm text-slate-400 leading-relaxed">{study.painPoint}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white mb-2 flex items-center"><Zap size={18} className="mr-2 text-[#FF6B35]"/> Agility & Hands-On Intervention</h4>
                      <p className="text-sm text-slate-400 leading-relaxed">{study.agility}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white mb-2 flex items-center"><Database size={18} className="mr-2 text-[#FF6B35]"/> Tech-Enabled Architecture</h4>
                      <p className="text-sm text-slate-400 leading-relaxed">{study.tech}</p>
                    </div>
                    <div className="bg-slate-900 p-5 rounded-xl border border-slate-700">
                      <h4 className="text-lg font-bold text-white mb-3 flex items-center"><TrendingUp size={18} className="mr-2 text-[#FF6B35]"/> Commercial Impact</h4>
                      <ul className="list-disc pl-5 space-y-2 text-sm text-slate-300">
                        {study.impact.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <button 
                  onClick={() => setExpandedCaseStudy(expandedCaseStudy === idx ? null : idx)}
                  className="mt-6 text-[#FF6B35] font-bold hover:text-white transition-colors flex items-center justify-center w-full bg-slate-900 py-3 rounded-xl border border-slate-700 hover:border-[#FF6B35]"
                >
                  {expandedCaseStudy === idx ? 'View Less' : 'View More on this Case Study'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer / Contact */}
      <footer id="contact" className="bg-[#0B132B] text-white py-20 border-t-8 border-[#FF6B35]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-extrabold mb-6">Have an Operational Bottleneck or Capital Constraint?</h2>
              <p className="text-slate-400 mb-8 max-w-md text-lg">Take our 5-Minute Enterprise Diagnostic Assessment or schedule an advisory session to evaluate your operational maturity and capital readiness.</p>
              <div className="space-y-4 text-slate-300 mb-10 font-medium">
                <p><strong>Email:</strong> info@xpanxn.co.za</p>
                <p><strong>Phone:</strong> +27 82 894 9157 | +27 78 406 0147</p>
                <p><strong>Address:</strong> Suite V, The Pivot, 1 Montecasino Boulevard, Fourways, 2191, South Africa</p>
              </div>
              
              {/* Social Media Links Using Generic SVG icons to prevent lucide-react export issues */}
              <div className="flex space-x-6">
                <a href="https://www.linkedin.com/company/xpanxn/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#FF6B35] transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                </a>
                <a href="https://www.youtube.com/@Xpanxn" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#FF6B35] transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
                </a>
                <a href="https://www.facebook.com/people/Xpanxn-B2B-Manufacturing/61576074647384/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#FF6B35] transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                </a>
              </div>
            </div>
            
            <div className="bg-slate-800 p-10 rounded-3xl flex flex-col items-center justify-center text-center shadow-2xl border border-slate-700">
              <Lightbulb size={56} className="text-[#FF6B35] mb-6" />
              <h3 className="text-3xl font-bold mb-4">Curious about your growth potential?</h3>
              <p className="text-slate-400 mb-10 font-medium">Take our intelligent diagnostic assessment to discover exactly which Xpanxn solutions fit your current business stage.</p>
              <button 
                onClick={() => setIsAssessmentOpen(true)}
                className="bg-[#FF6B35] text-white px-10 py-5 rounded-full text-xl font-extrabold hover:bg-[#e85b28] transition-all w-full shadow-lg hover:shadow-[#FF6B35]/30"
              >
                Start Enterprise Diagnostic
              </button>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-16 pt-8 text-center text-slate-500 text-sm font-medium">
            <span>© {new Date().getFullYear()} Xpanxn (Pty) Ltd. All Rights Reserved.</span>
          </div>
        </div>
      </footer>

      {/* Assessment Modal */}
      {isAssessmentOpen && <AssessmentModal onClose={() => setIsAssessmentOpen(false)} />}
    </div>
  );
}

// --- ASSESSMENT LOGIC & COMPONENT ---
function AssessmentModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    goal: '',
    stage: '',
    startupDetails: '',
    revenue: '',
    industry: '',
    customerChannels: [],
    formality: [],
    compliance: [],
    constraints: [],
    operationalConstraints: [],
    valueProp: '',
    marketAccess: '',
    fundingNeed: '',
    customerRisk: '',
    leadName: '',
    leadEmail: ''
  });
  const [result, setResult] = useState(null);

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(item => item !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const nextStep = () => {
    if (step === 1) {
      if (formData.goal === 'Funding') setStep(2); 
      else setStep(3); 
    } else if (step === 2) {
      if (formData.stage === 'Startup') setStep(2.5);
      else setStep(3); 
    } else if (step === 2.5) {
      calculateResult();
    } else if (step === 3) setStep(4);
    else if (step === 4) setStep(5);
    else if (step === 5) setStep(6);
    else if (step === 6) calculateResult();
  };

  const calculateResult = () => {
    let recommendation = [];
    
    if (formData.stage === 'Startup') {
      recommendation.push({
        id: 'X1',
        title: 'Strategy Consulting (Start-Up Evaluation)',
        desc: 'Based on your start-up status, Xpanxn will evaluate your funding requirements on a case-by-case basis through our advisory practice. We help build bankable narratives.'
      });
    } else {
      const needsFunds = formData.fundingNeed === 'Yes' || formData.goal === 'Funding';

      recommendation.push({
        id: 'T3',
        title: 'Turnkey Execution Engine (Tier 3)',
        desc: needsFunds 
          ? 'To support your capacity or growth needs, we deploy our Strategy Consulting framework to structure your financials into a bankable deal for lenders.' 
          : 'Your indicators suggest fundamental strategic or operational bottlenecks. We recommend deploying our Turnkey Execution to halt operational decline.'
      });
      recommendation.push({
        id: 'T2',
        title: 'Embedded Advisory (Tier 2)',
        desc: 'You need predictable growth and effective channel management. We build a robust sales pipeline and facilitate execution to acquire reliable revenue via our Business Development Agency.'
      });
    }

    setResult(recommendation);
    setStep(7);
  };

  const generateReportEmail = () => {
    const subject = encodeURIComponent(`New Xpanxn Assessment Lead: ${formData.leadName || 'Website Visitor'}`);
    let body = `New Lead Generated from Xpanxn Website Assessment:%0D%0A%0D%0A`;
    body += `Name: ${formData.leadName}%0D%0A`;
    body += `Email: ${formData.leadEmail}%0D%0A%0D%0A`;
    body += `--- ASSESSMENT DATA ---%0D%0A`;
    body += `Goal: ${formData.goal}%0D%0A`;
    if (formData.stage) body += `Stage: ${formData.stage}%0D%0A`;
    if (formData.startupDetails) body += `Startup Idea: ${formData.startupDetails}%0D%0A`;
    if (formData.revenue) body += `Revenue: ${formData.revenue}%0D%0A`;
    if (formData.industry) body += `Industry: ${formData.industry}%0D%0A`;
    if (formData.customerChannels.length) body += `Customer Channels: ${formData.customerChannels.join(', ')}%0D%0A`;
    if (formData.formality.length) body += `Business Planning: ${formData.formality.join(', ')}%0D%0A`;
    if (formData.compliance.length) body += `Compliance & Certifications: ${formData.compliance.join(', ')}%0D%0A`;
    if (formData.constraints.length) body += `Business Conditions: ${formData.constraints.join(', ')}%0D%0A`;
    if (formData.operationalConstraints.length) body += `Operational Constraints: ${formData.operationalConstraints.join(', ')}%0D%0A`;
    if (formData.valueProp) body += `Knows Value Prop: ${formData.valueProp}%0D%0A`;
    if (formData.marketAccess) body += `Needs Market Access: ${formData.marketAccess}%0D%0A`;
    if (formData.fundingNeed) body += `Needs Funding: ${formData.fundingNeed}%0D%0A`;
    if (formData.customerRisk) body += `Customer Risk Base: ${formData.customerRisk}%0D%0A%0D%0A`;
    
    body += `--- SYSTEM RECOMMENDATION ---%0D%0A`;
    result.forEach(r => {
      body += `${r.id} - ${r.title}%0D%0A`;
    });

    window.location.href = `mailto:info@xpanxn.co.za?subject=${subject}&body=${body}`;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0B132B]/80 backdrop-blur-md">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-[#0B132B] px-6 py-5 flex justify-between items-center text-white shrink-0 border-b-4 border-[#FF6B35]">
          <h3 className="text-xl font-bold">Xpanxn Enterprise Diagnostic</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-[#FF6B35] transition-colors p-1">
            <X size={24} />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 md:p-8 overflow-y-auto grow bg-slate-50">
          
          {step === 1 && (
            <div className="animate-fade-in">
              <h4 className="text-2xl font-bold text-[#0B132B] mb-6">What is your primary objective?</h4>
              <div className="grid gap-4">
                <button 
                  onClick={() => setFormData({...formData, goal: 'Business Growth'})}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${formData.goal === 'Business Growth' ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-slate-200 bg-white hover:border-[#0B132B]'}`}
                >
                  <h5 className="font-bold text-lg text-[#0B132B] mb-1">Business Growth</h5>
                  <p className="text-slate-600 text-sm font-medium">I want to scale operations, increase revenue, and optimize my strategy.</p>
                </button>
                <button 
                  onClick={() => setFormData({...formData, goal: 'Funding'})}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${formData.goal === 'Funding' ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-slate-200 bg-white hover:border-[#0B132B]'}`}
                >
                  <h5 className="font-bold text-lg text-[#0B132B] mb-1">Raising Capital</h5>
                  <p className="text-slate-600 text-sm font-medium">I am looking to secure funding for expansion or a new venture.</p>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in">
              <h4 className="text-2xl font-bold text-[#0B132B] mb-6">What stage is your business?</h4>
              <div className="grid gap-4">
                <button 
                  onClick={() => setFormData({...formData, stage: 'Startup'})}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${formData.stage === 'Startup' ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-slate-200 bg-white hover:border-[#0B132B]'}`}
                >
                  <h5 className="font-bold text-lg text-[#0B132B] mb-1">Start-up</h5>
                  <p className="text-slate-600 text-sm font-medium">Pre-revenue or early stage.</p>
                </button>
                <button 
                  onClick={() => setFormData({...formData, stage: 'Existing'})}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${formData.stage === 'Existing' ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-slate-200 bg-white hover:border-[#0B132B]'}`}
                >
                  <h5 className="font-bold text-lg text-[#0B132B] mb-1">Existing Business</h5>
                  <p className="text-slate-600 text-sm font-medium">Operational and generating revenue.</p>
                </button>
              </div>
            </div>
          )}

          {step === 2.5 && (
            <div className="animate-fade-in space-y-6">
              <div>
                <h4 className="text-2xl font-bold text-[#0B132B] mb-2">Tell us about your Start-up</h4>
                <p className="text-slate-600 mb-4 font-medium">We evaluate start-up funding requirements on a case-by-case basis.</p>
                <textarea 
                  className="w-full border-2 border-slate-200 rounded-xl p-4 min-h-[120px] focus:border-[#FF6B35] focus:ring-0"
                  placeholder="Briefly describe your business idea, required capital, and what feasibility work has been completed..."
                  value={formData.startupDetails}
                  onChange={(e) => setFormData({...formData, startupDetails: e.target.value})}
                ></textarea>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <h5 className="font-bold text-[#0B132B] mb-1 border-b border-slate-100 pb-2">Please provide your details before submitting:</h5>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">Full Name</label>
                  <input 
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-lg p-3 focus:border-[#FF6B35]"
                    value={formData.leadName}
                    onChange={(e) => setFormData({...formData, leadName: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">Email Address</label>
                  <input 
                    type="email"
                    className="w-full border-2 border-slate-200 rounded-lg p-3 focus:border-[#FF6B35]"
                    value={formData.leadEmail}
                    onChange={(e) => setFormData({...formData, leadEmail: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fade-in space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Annual Revenue Bracket</label>
                <select 
                  className="w-full border-2 border-slate-200 rounded-xl p-3 focus:border-[#FF6B35] bg-white"
                  value={formData.revenue}
                  onChange={(e) => setFormData({...formData, revenue: e.target.value})}
                >
                  <option value="">Select Bracket...</option>
                  <option value="R1-R5m">R1m - R5m</option>
                  <option value="R5-R10m">R5m - R10m</option>
                  <option value="R10-R50m">R10m - R50m</option>
                  <option value="R50-R200m">R50m - R200m</option>
                  <option value="R200m+">Over R200m</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Industry Type</label>
                <select 
                  className="w-full border-2 border-slate-200 rounded-xl p-3 focus:border-[#FF6B35] bg-white"
                  value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})}
                >
                  <option value="">Select Sub-sector...</option>
                  <optgroup label="Mining and Quarrying">
                    <option value="Gold">Gold</option>
                    <option value="Diamonds">Diamonds</option>
                    <option value="PGMs">PGMs</option>
                    <option value="Zinc">Zinc</option>
                    <option value="Lead">Lead</option>
                    <option value="Copper">Copper</option>
                    <option value="Uranium">Uranium</option>
                    <option value="Aggregates">Aggregates</option>
                    <option value="Chrome">Chrome</option>
                    <option value="Iron Ore">Iron Ore</option>
                    <option value="Manganese">Manganese</option>
                    <option value="Other Mining">Other Mining</option>
                  </optgroup>
                  <optgroup label="Finance, Real Estate & Business Services">
                    <option value="Financial Services">Financial Services</option>
                    <option value="Investment">Investment</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Business Consulting">Business Consulting</option>
                    <option value="Insurance">Insurance</option>
                    <option value="Other Finance/Business">Other Finance/Business</option>
                  </optgroup>
                  <optgroup label="Manufacturing">
                    <option value="Agro-processing">Agro-processing</option>
                    <option value="Automotive manufacturing">Automotive manufacturing</option>
                    <option value="Metals fabrication">Metals fabrication</option>
                    <option value="Chemicals">Chemicals</option>
                    <option value="Plastics & Rubber">Plastics & Rubber</option>
                    <option value="Clothing/textiles">Clothing/textiles</option>
                    <option value="Food & Bev">Food & Bev</option>
                    <option value="Machinery">Machinery</option>
                    <option value="Electronics">Electronics</option>
                    <option value="Other Manufacturing">Other Manufacturing</option>
                  </optgroup>
                  <optgroup label="Wholesale and Retail">
                    <option value="Retail Clothing">Retail Clothing</option>
                    <option value="Retail Food">Retail Food</option>
                    <option value="Catering">Catering</option>
                    <option value="Theme Parks">Theme Parks</option>
                    <option value="Hotel & Accommodation">Hotel & Accommodation</option>
                    <option value="Other Retail/Wholesale">Other Retail/Wholesale</option>
                  </optgroup>
                  <optgroup label="Agriculture, Forestry & Fishing">
                    <option value="Poultry">Poultry</option>
                    <option value="Cattle">Cattle</option>
                    <option value="Sheep & Goats">Sheep & Goats</option>
                    <option value="Pig Farm">Pig Farm</option>
                    <option value="Fisheries">Fisheries</option>
                    <option value="Field Crops (maize, wheat, sugarcane)">Field Crops (maize, wheat, sugarcane)</option>
                    <option value="Horticultural (citrus, fruit, wine)">Horticultural (citrus, fruit, wine)</option>
                  </optgroup>
                  <optgroup label="Transport & Storage">
                    <option value="Freight">Freight</option>
                    <option value="Logistics">Logistics</option>
                    <option value="Storage Rentals">Storage Rentals</option>
                    <option value="Other Transport/Storage">Other Transport/Storage</option>
                  </optgroup>
                  <optgroup label="Communications">
                    <option value="Telecommunications">Telecommunications</option>
                    <option value="Internet & Posting">Internet & Posting</option>
                  </optgroup>
                  <optgroup label="General Government Services">
                    <option value="Education">Education</option>
                    <option value="Public Administration">Public Administration</option>
                    <option value="Health Services">Health Services</option>
                  </optgroup>
                  <optgroup label="Energy, Electricity & Water Supply">
                    <option value="Solar">Solar</option>
                    <option value="Hydro">Hydro</option>
                    <option value="Nuclear">Nuclear</option>
                    <option value="Water well (Bore hole)">Water well (Bore hole)</option>
                    <option value="Other Energy/Water">Other Energy/Water</option>
                  </optgroup>
                  <optgroup label="Healthcare">
                    <option value="Medical Practice">Medical Practice</option>
                    <option value="Hospital">Hospital</option>
                    <option value="Pharmaceuticals">Pharmaceuticals</option>
                    <option value="Medicine">Medicine</option>
                    <option value="Other Healthcare">Other Healthcare</option>
                  </optgroup>
                  <optgroup label="Environmental & Waste Services">
                    <option value="Waste Management">Waste Management</option>
                    <option value="Recycling (paper, rubber, plastic, electronics)">Recycling</option>
                    <option value="Consulting">Consulting</option>
                    <option value="Other Environmental">Other Environmental</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Customer Channels (Select one or all)</label>
                <div className="flex flex-wrap gap-3">
                  {['B2B', 'B2C', 'B2G'].map(item => (
                    <label key={item} className="flex flex-1 items-center justify-center space-x-2 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                      <input 
                        type="checkbox" 
                        className="w-5 h-5 text-[#FF6B35] rounded"
                        checked={formData.customerChannels.includes(item)}
                        onChange={() => handleMultiSelect('customerChannels', item)}
                      />
                      <span className="text-sm font-medium text-slate-700">{item}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="animate-fade-in space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Level of Business Planning, Do you have the following? (Select all that apply)</label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {['Strategic Planning', 'Business Plan', 'Management Accounts', 'KPIs tracked', 'Sales & Marketing Plan'].map(item => (
                    <label key={item} className="flex items-start space-x-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <input 
                        type="checkbox" 
                        className="w-5 h-5 mt-0.5 text-[#FF6B35] rounded shrink-0"
                        checked={formData.formality.includes(item)}
                        onChange={() => handleMultiSelect('formality', item)}
                      />
                      <span className="text-sm text-slate-700 font-medium">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Compliance and Certifications (Select all that apply)</label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {['Audited AFS', 'VAT Registered', 'Share Certificates available', 'Operating Licenses', 'UIF Registration', 'ISO certifications', 'Environmental licenses', 'Other'].map(item => (
                    <label key={item} className="flex items-start space-x-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <input 
                        type="checkbox" 
                        className="w-5 h-5 mt-0.5 text-[#FF6B35] rounded shrink-0"
                        checked={formData.compliance.includes(item)}
                        onChange={() => handleMultiSelect('compliance', item)}
                      />
                      <span className="text-sm text-slate-700 font-medium">{item}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="animate-fade-in space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Business conditions (past 12 months) - Select all that apply</label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {['declining revenue', 'increasing costs', 'stable revenue', 'increased demand', 'constrained capacity'].map(item => (
                    <label key={item} className="flex items-start space-x-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <input 
                        type="checkbox" 
                        className="w-5 h-5 mt-0.5 text-[#FF6B35] rounded shrink-0"
                        checked={formData.constraints.includes(item)}
                        onChange={() => handleMultiSelect('constraints', item)}
                      />
                      <span className="text-sm text-slate-700 capitalize font-medium">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">Major Operational constraints (Select all that apply)</label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {['Lacking Key Human Resources', 'High equipment failure', 'Need new equipment', 'Quality issues', 'Late deliveries'].map(item => (
                    <label key={item} className="flex items-start space-x-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <input 
                        type="checkbox" 
                        className="w-5 h-5 mt-0.5 text-[#FF6B35] rounded shrink-0"
                        checked={formData.operationalConstraints.includes(item)}
                        onChange={() => handleMultiSelect('operationalConstraints', item)}
                      />
                      <span className="text-sm text-slate-700 font-medium">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 border-t border-slate-200 pt-6">
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">Do you clearly know your Value Proposition?</label>
                  <select 
                    className="w-full border-2 border-slate-200 rounded-xl p-3 bg-white focus:border-[#FF6B35]"
                    value={formData.valueProp}
                    onChange={(e) => setFormData({...formData, valueProp: e.target.value})}
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">Do you need Market Access / New Sales?</label>
                  <select 
                    className="w-full border-2 border-slate-200 rounded-xl p-3 bg-white focus:border-[#FF6B35]"
                    value={formData.marketAccess}
                    onChange={(e) => setFormData({...formData, marketAccess: e.target.value})}
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">Do you need Funding?</label>
                  <select 
                    className="w-full border-2 border-slate-200 rounded-xl p-3 bg-white focus:border-[#FF6B35]"
                    value={formData.fundingNeed}
                    onChange={(e) => setFormData({...formData, fundingNeed: e.target.value})}
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">Customer Risk (Number of core customers)</label>
                  <input 
                    type="number"
                    className="w-full border-2 border-slate-200 rounded-xl p-3 focus:border-[#FF6B35]"
                    placeholder="e.g., 3"
                    value={formData.customerRisk}
                    onChange={(e) => setFormData({...formData, customerRisk: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="animate-fade-in space-y-6 text-center py-8">
              <CheckCircle2 size={64} className="text-[#FF6B35] mx-auto mb-4" />
              <h4 className="text-2xl font-bold text-[#0B132B] mb-2">Assessment Complete!</h4>
              <p className="text-slate-600 font-medium mb-6">Enter your details below to generate your personalized Xpanxn roadmap.</p>
              
              <div className="max-w-sm mx-auto space-y-4 text-left">
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-1">Full Name</label>
                  <input 
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl p-3 focus:border-[#FF6B35]"
                    value={formData.leadName}
                    onChange={(e) => setFormData({...formData, leadName: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-1">Email Address</label>
                  <input 
                    type="email"
                    className="w-full border-2 border-slate-200 rounded-xl p-3 focus:border-[#FF6B35]"
                    value={formData.leadEmail}
                    onChange={(e) => setFormData({...formData, leadEmail: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 7 && result && (
            <div className="animate-fade-in py-4">
              <h4 className="text-2xl font-bold text-[#0B132B] mb-2">Your Recommended Roadmap</h4>
              <p className="text-slate-600 mb-6 border-b border-slate-200 pb-4 font-medium">Based on your inputs, here is how Xpanxn can accelerate your business.</p>
              
              <div className="space-y-4 mb-8">
                {result.map((rec, idx) => (
                  <div key={idx} className="bg-white border-l-4 border-[#FF6B35] p-5 rounded-r-xl shadow-sm">
                    <div className="flex items-center mb-2">
                      <span className="bg-[#0B132B] text-white text-xs font-bold px-2 py-1 rounded mr-3">{rec.id}</span>
                      <h5 className="font-bold text-lg text-[#0B132B]">{rec.title}</h5>
                    </div>
                    <p className="text-slate-600 text-sm font-medium">{rec.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-[#0B132B] rounded-xl p-6 text-center">
                <p className="text-slate-300 font-medium mb-4">Click below to send this report directly to our advisory team. We will review your profile and reach out to schedule a consultation.</p>
                <button 
                  onClick={generateReportEmail}
                  className="bg-[#FF6B35] text-white px-8 py-3 rounded-full font-bold hover:bg-[#e85b28] transition-all shadow-md inline-flex items-center"
                >
                  Submit & Email My Report <ChevronRight className="ml-2" size={20} />
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Footer Actions */}
        {step < 7 && (
          <div className="bg-white border-t border-slate-200 px-6 py-4 flex justify-between items-center shrink-0">
            {step > 1 ? (
              <button 
                onClick={() => setStep(step === 2.5 ? 2 : step - 1)}
                className="text-slate-500 font-bold hover:text-[#0B132B] px-4 py-2 transition-colors"
              >
                Back
              </button>
            ) : <div></div>}
            
            <button 
              onClick={nextStep}
              disabled={
                (step === 1 && !formData.goal) || 
                (step === 2 && !formData.stage) ||
                (step === 2.5 && (!formData.leadName || !formData.leadEmail))
              }
              className="bg-[#0B132B] text-white px-8 py-3 rounded-full font-bold hover:bg-[#1a2542] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {step === 2.5 || step === 6 ? 'View Results' : 'Continue'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}